import type { ProactiveGreetingCacheEntry } from './proactive-greeting-cache.js';

export type GreetingOfferStatus = 'ready' | 'shown' | 'dismissed' | 'committed';

export type GreetingOfferState = {
  greetingId: string;
  status: GreetingOfferStatus;
  shownAt?: number;
};

const OFFER_STORAGE_PREFIX = 'teo_greeting_offer:';
const COMMITTED_STORAGE_PREFIX = 'teo_greeting_committed:';

function offerStorageKey(slug: string, pathname: string): string {
  return `${OFFER_STORAGE_PREFIX}${slug}:${pathname}`;
}

function committedStorageKey(slug: string): string {
  return `${COMMITTED_STORAGE_PREFIX}${slug}`;
}

function sessionStore(): Storage | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    return sessionStorage;
  } catch {
    return null;
  }
}

function localStore(): Storage | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage;
  } catch {
    return null;
  }
}

/** Stable id for idempotent commit — API `greeting_id` or client fallback. */
export function computeGreetingId(
  slug: string,
  pathname: string,
  options: {
    apiGreetingId?: string | null;
    fetchedAt: number | null;
    phraseCount: number;
  },
): string {
  const fromApi = options.apiGreetingId?.trim();
  if (fromApi) return fromApi;
  return `client:${slug}:${pathname}:${options.fetchedAt ?? 0}:${options.phraseCount}`;
}

export function resolveGreetingIdFromEntry(
  entry: Pick<
    ProactiveGreetingCacheEntry,
    'slug' | 'pathname' | 'greetingId' | 'fetchedAt' | 'greetingPhrases'
  >,
): string {
  return computeGreetingId(entry.slug, entry.pathname, {
    apiGreetingId: entry.greetingId,
    fetchedAt: entry.fetchedAt,
    phraseCount: entry.greetingPhrases.length,
  });
}

function readOfferState(
  slug: string,
  pathname: string,
): GreetingOfferState | null {
  const storage = sessionStore();
  if (!storage) return null;
  try {
    const raw = storage.getItem(offerStorageKey(slug, pathname));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GreetingOfferState;
    if (!parsed?.greetingId || !parsed.status) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeOfferState(
  slug: string,
  pathname: string,
  state: GreetingOfferState,
): void {
  const storage = sessionStore();
  if (!storage) return;
  try {
    storage.setItem(offerStorageKey(slug, pathname), JSON.stringify(state));
  } catch {
    // ignore quota / private mode
  }
}

function readCommittedIds(slug: string): Set<string> {
  const storage = localStore();
  if (!storage) return new Set();
  try {
    const raw = storage.getItem(committedStorageKey(slug));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return new Set();
  }
}

function writeCommittedIds(slug: string, ids: Set<string>): void {
  const storage = localStore();
  if (!storage) return;
  try {
    storage.setItem(
      committedStorageKey(slug),
      JSON.stringify([...ids].slice(-200)),
    );
  } catch {
    // ignore
  }
}

export function isGreetingIdCommitted(slug: string, greetingId: string): boolean {
  return readCommittedIds(slug).has(greetingId);
}

export function getGreetingOfferState(
  slug: string,
  pathname: string,
): GreetingOfferState | null {
  return readOfferState(slug, pathname);
}

export function markGreetingOfferShown(
  slug: string,
  pathname: string,
  greetingId: string,
): void {
  const existing = readOfferState(slug, pathname);
  // Already committed into the thread — do not revive as pending UI.
  if (existing?.status === 'committed') {
    return;
  }
  if (existing?.greetingId === greetingId && existing.status === 'shown') {
    return;
  }
  writeOfferState(slug, pathname, {
    greetingId,
    status: 'shown',
    shownAt: Date.now(),
  });
}

export function markGreetingOfferShownFromEntry(
  entry: ProactiveGreetingCacheEntry,
): void {
  if (!entry.shouldShow || entry.greetingPhrases.length === 0) return;
  markGreetingOfferShown(
    entry.slug,
    entry.pathname,
    resolveGreetingIdFromEntry(entry),
  );
}

export function markGreetingOfferDismissed(
  slug: string,
  pathname: string,
  greetingId?: string,
): void {
  const existing = readOfferState(slug, pathname);
  const id = greetingId ?? existing?.greetingId;
  if (!id) return;
  if (existing?.status === 'committed') return;
  writeOfferState(slug, pathname, {
    greetingId: id,
    status: 'dismissed',
    shownAt: existing?.shownAt,
  });
}

export function markGreetingOfferCommitted(
  slug: string,
  pathname: string,
  greetingId: string,
): void {
  writeOfferState(slug, pathname, {
    greetingId,
    status: 'committed',
    shownAt: readOfferState(slug, pathname)?.shownAt,
  });
  const committed = readCommittedIds(slug);
  committed.add(greetingId);
  writeCommittedIds(slug, committed);
}

/** Offer can be sent with the next user message (shown, not dismissed/committed). */
export function isGreetingOfferCommittable(
  slug: string,
  pathname: string,
  greetingId: string,
): boolean {
  if (isGreetingIdCommitted(slug, greetingId)) return false;
  const state = readOfferState(slug, pathname);
  if (!state || state.greetingId !== greetingId) return false;
  return state.status === 'shown';
}

/** Clears per-path offer state so a fresh page visit can show/commit again.
 * Committed greeting_id set in localStorage is kept for send idempotency. */
export function clearGreetingOfferState(
  slug: string,
  pathname: string,
): void {
  const storage = sessionStore();
  if (!storage) return;
  try {
    storage.removeItem(offerStorageKey(slug, pathname));
  } catch {
    // ignore
  }
}

/** Clears offer + committed flags (playground / support). */
export function clearGreetingOfferSessionState(slug?: string): string[] {
  const keys: string[] = [];
  for (const storage of [sessionStore(), localStore()]) {
    if (!storage) continue;
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (
        key &&
        (key.startsWith(OFFER_STORAGE_PREFIX) ||
          key.startsWith(COMMITTED_STORAGE_PREFIX)) &&
        (!slug || key.includes(slug))
      ) {
        keys.push(key);
      }
    }
  }
  for (const storage of [sessionStore(), localStore()]) {
    if (!storage) continue;
    for (const key of keys) {
      if (storage.getItem(key) != null) {
        storage.removeItem(key);
      }
    }
  }
  return keys;
}
